
<template>
    <div class="vue-csv-uploader">
        <div class="form">
            <div class="vue-csv-uploader-part-one">
                <div class="form-check form-group csv-import-checkbox" v-if="headers === null">
                    <slot name="hasHeaders" :headers="hasHeaders" :toggle="toggleHasHeaders">
                        <input :class="checkboxClass" type="checkbox" :id="makeId('hasHeaders')" :model-value="hasHeaders" @change="toggleHasHeaders">
                        <label class="form-check-label" :for="makeId('hasHeaders')">
                            File Has Headers
                        </label>
                    </slot>
                </div>
                <div class="form-group csv-import-file">
                    <p-button @click="triggerFileInput" variant="outline-primary" >Choose File</p-button>
                    <span v-if="fileName" class="ps-2">{{ fileName }}</span>
                    <input ref="csv" type="file" @change.prevent="validFileMimeType" :class="inputClass" name="csv" :key="inputKey" style="display: none">
                    <slot name="error" v-if="showErrorMessage">
                        <div class="invalid-feedback d-block">
                            File type is invalid
                        </div>
                    </slot>
                </div>
                <div class="input-group">
                    <slot name="next" :load="load">
                        <p-button type="submit" :disabled="disabledNextButton" :is-busy="isJobRunBusy" :variant="buttonVariant" @click.prevent="load">
                            {{ loadBtnText }}
                        </p-button>
                    </slot>
                    <slot name="runNoParse" >
                        <p-button
                            v-if="showRunNoParseButton"
                            :disabled="disabledRunNoParseButton"
                            :is-busy="isJobRunBusy"
                            :variant="buttonVariant"
                            class="ms-1"
                            @click.prevent="handleRunNoParse"
                        >
                            {{ runNoParseBtnTxt }}
                        </p-button>
                    </slot>
                </div>
            </div>
             <div class="vue-csv-uploader-part-two">
                <div class="vue-csv-mapping" v-if="sample && showFieldMapping">
                    <table :class="tableClass">
                        <slot name="thead">
                            <thead>
                            <tr>
                                <th>Field</th>
                                <th>CSV Column</th>
                            </tr>
                            </thead>
                        </slot>
                        <tbody>
                        <tr v-for="(field, key) in fieldsToMap" :key="key">
                            <td>{{ field.label }}</td>
                            <td>
                                <select :class="tableSelectClass" :name="`csv_uploader_map_${key}`" v-model="map[field.key]">
                                    <option :value="null" v-if="canIgnore">Ignore</option>
                                    <option v-for="(column, key) in firstRow" :key="key" :value="key">{{ column }}</option>
                                </select>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    <div class="form-group" v-if="url">
                        <slot name="submit" :submit="submit">
                            <input type="submit" :variant="buttonVariant" @click.prevent="submit" :model-value="submitBtnText">
                        </slot>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { drop, every, forEach, get, isArray, map, set } from 'lodash';
    import axios from 'axios';
    import Papa from 'papaparse';
    import mimeTypes from "mime-types";

    export default {
        props: {
            value: Array,
            url: {
                type: String
            },
            mapFields: {
                required: true
            },
            callback: {
                type: Function,
                default: () => ({})
            },
            catch: {
                type: Function,
                default: () => ({})
            },
            finally: {
                type: Function,
                default: () => ({})
            },
            parseConfig: {
                type: Object,
                default() {
                    return {};
                }
            },
            headers: {
                default: null
            },
            loadBtnText: {
                type: String,
                default: "Parse File"
            },
            submitBtnText: {
                type: String,
                default: "Submit"
            },
            autoMatchFields: {
                type: Boolean,
                default: false
            },
            showFieldMapping: {
                type: Boolean,
                default: false
            },
            autoMatchIgnoreCase: {
                type: Boolean,
                default: false
            },
            tableClass: {
                type: String,
                default: "table"
            },
            checkboxClass: {
                type: String,
                default: "form-check-input"
            },
            buttonVariant: {
                type: String,
                default: "primary"
            },
            inputClass: {
                type: String,
                default: "form-control-file"
            },
            validation: {
                type: Boolean,
                default: true,
            },
            fileMimeTypes: {
                type: Array,
                default: () => {
                    return ["text/csv", "text/x-csv", "application/vnd.ms-excel", "text/plain"];
                }
            },
            tableSelectClass: {
                type: String,
                default: 'form-control'
            },
            canIgnore: {
               type: Boolean,
               default: false,
            },
            canRunNoParse: {
                type: Boolean,
                default: false,
            },
            runNoParseBtnTxt: {
                type: String,
                default: 'Import Without Parsing'
            },
            isJobRunningNow: {
                type: Boolean,
                default: false,
            }
        },
        data: () => ({
            form: {
                csv: null,
            },
            isBusy:false,
            fieldsToMap: [],
            map: {},
            hasHeaders: true,
            csv: null,
            sample: null,
            isValidFileMimeType: false,
            fileSelected: false,
            isRunNowTriggered: false,
            fileName: "",
            inputKey: 0
        }),
        created() {
            this.hasHeaders = this.headers;

            if (isArray(this.mapFields)) {
                this.fieldsToMap = map(this.mapFields, (item) => {
                    return {
                        key: item,
                        label: item
                    };
                });
            } else {
                this.fieldsToMap = map(this.mapFields, (label, key) => {
                    return {
                        key: key,
                        label: label
                    };
                });
            }
        },
        methods: {
            submit() {
                const _this = this;
                if (this.url) {
                    axios.post(this.url, this.form)
                        .then(response => {
                            _this.callback(response);
                        })
                        .catch(response => {
                            _this.catch(response);
                        })
                        .finally(response => {
                            this.$refs.csv = null;
                            _this.finally(response);
                        });
                } else {
                    _this.callback(this.form.csv);
                }
            },
            triggerFileInput() {
                if (this._openingFile) return;
                this._openingFile = true;
                this.$refs.csv.value = '';
                this.$nextTick(() => {
                    this.$refs.csv.click();
                    setTimeout(() => { this._openingFile = false; }, 500);
                });
            },
            buildMappedCsv() {
                const _this = this;

                let csv = this.hasHeaders ? drop(this.csv) : this.csv;

                return map(csv, (row) => {
                    let newRow = {};

                    forEach(_this.map, (column, field) => {
                        set(newRow, field, get(row, column));
                    });

                    return newRow;
                });
            },
            validFileMimeType() {
                let file = this.$refs.csv.files[0];

                if (file) {
                    const mimeType = file.type === "" ? mimeTypes.lookup(file.name) : file.type;
                    this.fileSelected = true;
                    this.isValidFileMimeType = this.validation ? this.validateMimeType(mimeType) : true;
                    this.fileName = file.name;
                    this.$emit('loading-new-file');
                } else {
                    this.isValidFileMimeType = !this.validation;
                    this.fileSelected = false;
                }
            },
            validateMimeType(type) {
                return this.fileMimeTypes.indexOf(type) > -1;
            },
            load() {
                this.isBusy = true;
                this.$emit('loading-new-file');
                this.map = {};
                const _this = this;

                setTimeout(() => {
                    this.readFile((output) => {
                        _this.sample = get(Papa.parse(output, { preview: 2, skipEmptyLines: true}), "data");
                        _this.csv = get(Papa.parse(output, { skipEmptyLines: true }), "data");
                        _this.$emit('file-name', _this.fileName);
                    });
                }, 100);
            },
            readFile(callback) {
                let _this = this;
                let file = this.$refs.csv.files[0];

                if (file) {
                    let reader = new FileReader();
                    reader.onload = function (evt) {
                        _this.isBusy = false;
                        callback(evt.target.result);
                    };
                    reader.readAsText(file, "UTF-8");
                    reader.onerror = function (event) {
                        _this.$emit('error-loading-file', event);
                        //run into error if the file is modified after being loaded.  we no longer have permissions to load the file
                        //DOMException: The requested file could not be read, typically due to permission problems that have occurred after a reference to a file was acquired.
                        //"The requested file could not be read, typically due to permission problems that have occurred after a reference to a file was acquired."
                        _this.isBusy = false;
                    };
                }
            },
            toggleHasHeaders() {
                this.hasHeaders = !this.hasHeaders;
            },
            makeId(id) {
                return `${id}${this.$.uid}`;
            },
            handleRunNoParse() {
                this.isRunNowTriggered = true;
                let file = this.$refs.csv.files[0];
                this.$emit('run-no-parse', file);
            }
        },
        watch: {
            map: {
                deep: true,
                handler: function (newVal) {
                    if (!this.url) {
                        let hasAllKeys = Array.isArray(this.mapFields) ? every(this.mapFields, function (item) {
                            return newVal.hasOwnProperty(item);
                        }) : every(this.mapFields, function (item, key) {
                            return newVal.hasOwnProperty(key);
                        });

                        if (hasAllKeys) {
                            this.form.csv = this.buildMappedCsv();
                            this.$emit('input', this.form.csv);
                        }
                    }
                },
            },
            sample(newVal) {
                if(this.autoMatchFields){
                    if(newVal !== null){
                        this.fieldsToMap.forEach(field => {
                            newVal[0].forEach((columnName, index) => {
                                if(this.autoMatchIgnoreCase === true){
                                    if(field.label.toLowerCase().trim() === columnName.toLowerCase().trim()){
                                        this.map[field.key] = index;
                                    }
                                } else{
                                    if(field.label.trim() === columnName.trim()){
                                        this.map[field.key] = index;
                                    }
                                }
                            });
                        });

                        this.$emit('unmapped-fields',this.mapFields.filter(x => this.map[x]  === undefined));
                    }
                    else{
                       this.$emit('unmapped-fields',[]);
                   }
                }
            },
            isJobRunningNow: {
                handler (value) {
                    this.isRunNowTriggered = value;
                },
                immediate: true
            }
        },
        computed: {
            firstRow() {
                return get(this, "sample.0");
            },
            showErrorMessage() {
                return this.fileSelected && !this.isValidFileMimeType;
            },
            disabledNextButton() {
                return !this.isValidFileMimeType || !this.fileSelected;
            },
            showRunNoParseButton() {
                return this.canRunNoParse;
            },
            disabledRunNoParseButton() {
                return this.showRunNoParseButton && !this.isValidFileMimeType;
            },
            isJobRunBusy() {
                return this.isBusy || this.isRunNowTriggered;
            }
        },
    };
</script>
